import React, { RefObject, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { supabase } from "../../../../lib/supabase";

type Product = {
  id: string | number;
  name: string;
  image_url?: string | null;
  created_at: string;
};

type NewProductsMenuProps = {
  isOpen: boolean;
  anchorRef: RefObject<HTMLElement | null>;
  onClose: () => void;
  variant?: "desktop" | "mobile";
  onBack?: () => void;
};

const NewProductsMenu: React.FC<NewProductsMenuProps> = ({
  isOpen,
  anchorRef,
  onClose,
  variant = "desktop",
  onBack,
}) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchLatestProducts = async () => {
      setLoading(true);

      const { data, error } = await supabase
        .from("Product")
        .select("id, name, image_url, created_at")
        .order("created_at", { ascending: false })
        .limit(5);

      if (error) {
        console.error("Error fetching latest products:", error.message);
        setProducts([]);
      } else {
        setProducts(data || []);
      }

      setLoading(false);
    };

    if (isOpen) {
      fetchLatestProducts();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  if (variant === "mobile") {
    return (
      <div className="flex flex-col">
        <button
          type="button"
          onClick={onBack}
          className="
            mb-2
            flex items-center gap-2
            px-3 py-2
            rounded-xl
            text-[#5c2f2f]
            font-semibold
            hover:bg-[#f7dada]/50
            transition
          "
        >
          <ChevronLeft size={18} />
          Back
        </button>

        {loading ? (
          <div className="px-3 py-4 text-[#7a4343] text-sm">Loading...</div>
        ) : products.length === 0 ? (
          <div className="px-3 py-4 text-[#7a4343] text-sm">
            No new products found.
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {products.map((product) => (
              <Link
                key={product.id}
                to={`/product/${product.id}`}
                onClick={onClose}
                className="
                  flex items-start gap-3
                  px-3 py-3
                  rounded-xl
                  hover:bg-[#f7dada]/50
                  transition
                "
              >
                <img
                  src={product.image_url || "/placeholder.png"}
                  alt={product.name}
                  className="w-20 h-20 object-cover rounded-lg border border-[#efd6d6] shrink-0"
                />

                <div className="h-20 flex items-center min-w-0 overflow-hidden">
                  <div
                    className="
                      text-[#5c2f2f]
                      font-semibold
                      text-sm
                      leading-5
                      overflow-hidden
                      line-clamp-2
                      break-words
                    "
                  >
                    {product.name}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    );
  }

  const leftPosition = anchorRef.current
    ? anchorRef.current.offsetLeft - 110
    : 0;

  return (
    <div
      className="
        absolute top-full mt-4
        min-w-[360px]
        rounded-2xl
        bg-white/95 backdrop-blur-xl
        border border-[#f1dcdc]
        shadow-[0_18px_55px_rgba(0,0,0,0.18)]
        p-4
      "
      style={{ left: leftPosition }}
    >
      <div className="mb-3 text-[#5c2f2f] font-bold uppercase tracking-widest text-sm">
        New Products
      </div>

      {loading ? (
        <div className="py-4 text-[#7a4343] text-sm">Loading...</div>
      ) : products.length === 0 ? (
        <div className="py-4 text-[#7a4343] text-sm">
          No new products found.
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {products.map((product) => (
            <Link
              key={product.id}
              to={`/product/${product.id}`}
              onClick={onClose}
              className="
                flex items-start gap-3
                rounded-xl
                p-2
                hover:bg-[#fff4f4]
                transition
              "
            >
              <img
                src={product.image_url || "/placeholder.png"}
                alt={product.name}
                className="w-20 h-20 object-cover rounded-lg border border-[#efd6d6] shrink-0"
              />

              <div className="h-20 flex items-center min-w-0 overflow-hidden">
                <div
                  className="
                    text-[#5c2f2f]
                    font-semibold
                    text-sm
                    leading-5
                    overflow-hidden
                    line-clamp-2
                    break-words
                  "
                >
                  {product.name}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default NewProductsMenu;